import { makeStyles } from "@material-ui/core/styles";
import CardSpacer from "@saleor/components/CardSpacer";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Money from "@saleor/components/Money";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import Skeleton from "@saleor/components/Skeleton";
import { TopProducts_reportProductSales_edges_node } from "@saleor/home/types/TopProducts";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { SearchBrandsPrivate_search_edges_node } from "@saleor/searches/types/SearchBrandsPrivate";
import { SearchStores_search_edges_node } from "@saleor/searches/types/SearchStores";
import { UserPermissionProps } from "@saleor/types";
import { FetchMoreProps } from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Orders from "../../../icons/Orders";
import Sales from "../../../icons/Sales";
import { Home_storeOrdersTotal_gross } from "../../types/Home";
import HomeAnalyticsCard from "../HomeAnalyticsCard";
import HomeHeader from "../HomeHeader";
import HomeNotificationTable from "../HomeNotificationTable";
import HomeProductListCard from "../HomeProductListCard";
const useStyles = makeStyles(
  theme => ({
    cardContainer: {
      display: "grid",
      gridColumnGap: theme.spacing(3),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("sm")]: {
        gridColumnGap: theme.spacing(1)
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    icon: {
      "& path": {
        fill: theme.palette.primary.main
      }
    }
  }),
  { name: "HomePage" }
);

export interface SellerBrandFormData {
  sellerBrand: string;
}

export interface HomePageProps extends UserPermissionProps {
  // activities: Home_activities_edges_node[];
  brands: SearchBrandsPrivate_search_edges_node[];
  brandLabel: string;
  brandID: string;
  orders: number;
  motherOrders: number;
  storeOrders: number;
  // productsOutOfStock: number;
  sales: Home_storeOrdersTotal_gross;
  showTenants: boolean;
  topProducts: TopProducts_reportProductSales_edges_node[];
  tenants?: SearchStores_search_edges_node[];
  userName: string;
  onMotherOrdersClick: () => void;
  onStoreOrdersClick: () => void;
  onProductClick: (productId: string, variantId: string) => void;
  // onProductsOutOfStockClick: () => void;
  fetchBrands: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;
  fetchTenants: (query: string) => void;
  onChangeStore: (id: string) => void;
  onSubmit: (data: SellerBrandFormData) => void;
}

const HomePage: React.FC<HomePageProps> = props => {
  const {
    brands,
    brandLabel,
    brandID,
    userName,
    orders,
    sales,
    showTenants,
    topProducts,
    onProductClick,
    // activities,
    onMotherOrdersClick,
    onStoreOrdersClick,
    // onProductsOutOfStockClick,
    motherOrders,
    storeOrders,
    // productsOutOfStock,
    userPermissions,
    tenants,
    fetchBrands,
    fetchTenants,
    fetchMoreBrands,
    onChangeStore,
    onSubmit
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const { user } = useUser();
  const [currentStoreId, setCurrentStoreId] = useState("");
  const [currentStoreName, setCurrentStoreName] = useState("");
  // console.log(brandID);
  const initialForm: SellerBrandFormData = {
    sellerBrand: ""
  };

  useEffect(() => {
    if (currentStoreId) {
      onChangeStore(currentStoreId);
    }
  }, [currentStoreId]);

  const storeChoices = maybe(() => tenants, []).map(tenant => ({
    label: tenant.storeName,
    value: tenant.id
  }));

  const sellerBrandChoices = maybe(
    () =>
      brands.map(brand => ({
        label: brand?.name + " - " + brand.legalName,
        value: brand?.id
      })),
    []
  );

  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");
  // const [hasBrandID, setHasBrandID] = useState(true);
  useEffect(() => {
    // console.log('brandLabel');
    setSellerDisplayName(brandLabel);
  }, [brandLabel]);

  const handleSubmit = event => {
    onSubmit(event);
  };
  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => {
        useEffect(() => {
          if (formData.sellerBrand !== "") {
            submit();
          }
        }, [formData]);

        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerBrandChoices
        );
        return (
          <Container>
            <HomeHeader
              brandID={brandID}
              brands={sellerBrandChoices}
              userName={userName}
              user={user}
              data={formData}
              change={change}
              fetchBrands={query => fetchBrands(query)}
              fetchMoreBrands={fetchMoreBrands}
              onBrandChange={event => {
                handleSelectBrand(event);
                setCurrentStoreName("");
                setCurrentStoreId("");
              }}
              sellerDisplayValue={sellerDisplayName}
              submit={submit}
            />
            <CardSpacer />
            {/* <Grid> */}
            <div>
              {showTenants && (
                <SingleAutocompleteSelectField
                  // allowCustomValues={false}
                  choices={storeChoices}
                  displayValue={currentStoreName}
                  fetchChoices={fetchTenants}
                  // hasMore={hasMoreUsers}
                  // loading={loading}
                  label="Store"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Search Stores"
                  })}
                  onChange={e => {
                    setCurrentStoreId(e.target.value);
                    setCurrentStoreName(
                      storeChoices.find(store => store.value === e.target.value)
                        .label
                    );
                  }}
                  // onFetchMore={onFetchMoreUsers}
                  name="storeId"
                  value={currentStoreId}
                />
              )}
              <CardSpacer />
              {(user.userType.code === SmopUserTypeCode.SELLER_MASTER ? (
                currentStoreId !== null
              ) : (
                true
              )) ? (
                <>
                  <div className={classes.cardContainer}>
                    <HomeAnalyticsCard
                      title={"Sales"}
                      icon={
                        <Sales
                          className={classes.icon}
                          fontSize={"inherit"}
                          viewBox="0 0 64 64"
                        />
                      }
                    >
                      {sales ? (
                        <Money money={sales} />
                      ) : (
                        <Skeleton style={{ width: "5em" }} />
                      )}
                    </HomeAnalyticsCard>
                    <HomeAnalyticsCard
                      title={"Orders"}
                      icon={
                        <Orders
                          className={classes.icon}
                          fontSize={"inherit"}
                          viewBox="0 0 64 64"
                        />
                      }
                    >
                      {orders === undefined ? (
                        <Skeleton style={{ width: "5em" }} />
                      ) : (
                        orders
                      )}
                    </HomeAnalyticsCard>
                  </div>
                  <HomeNotificationTable
                    motherOrders={motherOrders}
                    storeOrders={storeOrders}
                    onMotherOrdersClick={onMotherOrdersClick}
                    onStoreOrdersClick={onStoreOrdersClick}
                    userPermissions={userPermissions}
                  />
                  <CardSpacer />
                  <HomeProductListCard
                    onRowClick={onProductClick}
                    topProducts={topProducts}
                  />
                </>
              ) : (
                <FormattedMessage
                  defaultMessage="Please select a store."
                  description="select store notif"
                />
              )}
            </div>
          </Container>
        );
      }}
    </Form>
  );
};
HomePage.displayName = "HomePage";
export default HomePage;
